import { useEffect, useState } from "react"
import { ArrowLeftRight, Copy } from "lucide-react"

const DEBOUNCE_MS = 400

function encodeForUrl(text: string): string {
  return encodeURIComponent(text)
}

function decodeFromUrl(text: string): string | null {
  try {
    return decodeURIComponent(text.replace(/\+/g, "%20"))
  } catch {
    return null
  }
}

/** Left panel is always the editable source; swap flips which field is on the left. */
type LeftField = "decoded" | "encoded"

export default function UrlEncoderTab() {
  const [leftField, setLeftField] = useState<LeftField>("decoded")
  const [decoded, setDecoded] = useState("")
  const [encoded, setEncoded] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [copyState, setCopyState] = useState<"idle" | "copied">("idle")

  const encoding = leftField === "decoded"

  // Left = Decoded → fill Encoded
  useEffect(() => {
    if (!encoding) return
    setError(null)
    const timer = window.setTimeout(() => {
      setEncoded(encodeForUrl(decoded))
    }, DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [decoded, encoding])

  // Left = Encoded → fill Decoded
  useEffect(() => {
    if (encoding) return
    const timer = window.setTimeout(() => {
      if (!encoded) {
        setDecoded("")
        setError(null)
        return
      }
      const next = decodeFromUrl(encoded)
      if (next === null) {
        setError("Couldn’t decode — wait until the encoded text is valid.")
        return
      }
      setError(null)
      setDecoded(next)
    }, DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [encoded, encoding])

  function handleSwapFields() {
    setError(null)
    setLeftField((prev) => (prev === "decoded" ? "encoded" : "decoded"))
  }

  async function handleCopy() {
    if (!encoded) return
    try {
      await navigator.clipboard.writeText(encoded)
      setCopyState("copied")
      setTimeout(() => setCopyState("idle"), 2000)
    } catch {
      const el = document.createElement("textarea")
      el.value = encoded
      document.body.appendChild(el)
      el.select()
      document.execCommand("copy")
      document.body.removeChild(el)
      setCopyState("copied")
      setTimeout(() => setCopyState("idle"), 2000)
    }
  }

  const decodedPanel = (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
      <fieldset
        className={[
          "flex min-h-[12rem] flex-1 flex-col rounded border px-4 pb-4 pt-1 transition-colors",
          encoding ? "border-[#8b9aff]" : "border-[#555]",
        ].join(" ")}
      >
        <legend className="px-1 text-sm text-[#aaa]">
          Decoded{encoding ? " (type here)" : ""}
        </legend>
        <textarea
          value={decoded}
          onChange={(e) => setDecoded(e.target.value)}
          readOnly={!encoding}
          placeholder="Plain text…"
          className={[
            "min-h-[8rem] w-full flex-1 resize-y rounded border-0 bg-transparent text-sm outline-none placeholder:text-[#666]",
            encoding ? "text-white" : "cursor-default text-[#b0b0b0]",
          ].join(" ")}
        />
      </fieldset>
    </div>
  )

  const encodedPanel = (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <fieldset
        className={[
          "flex min-h-[12rem] flex-1 flex-col rounded border px-4 pb-4 pt-1 transition-colors",
          !encoding ? "border-[#8b9aff]" : "border-[#555]",
        ].join(" ")}
      >
        <legend className="px-1 text-sm text-[#aaa]">
          Encoded{!encoding ? " (type here)" : ""}
        </legend>
        <textarea
          value={encoded}
          onChange={(e) => setEncoded(e.target.value)}
          readOnly={encoding}
          placeholder="URL-encoded text…"
          className={[
            "min-h-[8rem] w-full flex-1 resize-y rounded border-0 bg-transparent text-sm outline-none placeholder:text-[#666]",
            !encoding ? "text-white" : "cursor-default text-[#b0b0b0]",
          ].join(" ")}
        />
      </fieldset>
    </div>
  )

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b-2 border-[#8b9aff] pb-2">
        <h2 className="text-2xl font-semibold text-white">URL Encoder</h2>
        <p className="text-sm text-[#9eabff]">
          {encoding ? "Decoded → Encoded" : "Encoded → Decoded"}
          <span className="text-[#888]"> · live after pause</span>
        </p>
      </div>

      <div className="flex flex-col gap-3 md:flex-row md:items-stretch md:gap-3">
        {encoding ? decodedPanel : encodedPanel}

        <div className="flex shrink-0 items-center justify-center md:flex-col md:px-1">
          <button
            type="button"
            onClick={handleSwapFields}
            title="Swap field positions"
            aria-label="Swap field positions"
            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-[#555] bg-[#3a3a3a] text-white transition-colors hover:bg-[#444] md:self-center"
          >
            <ArrowLeftRight className="h-4 w-4" aria-hidden />
          </button>
        </div>

        {encoding ? encodedPanel : decodedPanel}
      </div>

      {error ? <p className="text-sm text-[#f87171]">{error}</p> : null}

      <button
        type="button"
        onClick={handleCopy}
        disabled={!encoded}
        className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-[#555] bg-[#3a3a3a] px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-[#444] disabled:cursor-not-allowed disabled:opacity-40"
      >
        <Copy className="h-4 w-4" aria-hidden />
        {copyState === "copied" ? "Copied" : "Copy"}
      </button>
    </div>
  )
}
